/**
 * §9's inline document workspace — the paper the three generated documents are rendered on.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * §9'S CRITICAL RENDERING RULE, WHICH IS THE WHOLE REASON THIS FILE EXISTS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * > **Critical rendering rule (a bug caught in review):** issued documents must render into a
 * > **read-only document view**, never a textarea. A textarea is invisible to `innerText`,
 * > visually flat, and made the verification impossible. Issued documents get
 * > `Reopen For Editing`.
 *
 * So {@link DocumentView} is a `<div>` whose text is a text node: it is selectable, it is in
 * the accessibility tree, `innerText` sees it, a test can assert on it, and — the point that
 * matters at a tournament — the browser will print it. A draft is a `<textarea>` because a
 * draft is being typed into; the moment it is issued it stops being one.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE LETTERHEAD IS APPLIED HERE AND IS NOT IN THE STRING
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `packages/shared/src/hearing-documents.ts` says so in its own header: the generators return
 * plain text and *"the letterhead … is applied by the document view when it renders, not
 * baked into the string, so the same body can be shown on screen, printed, and later written
 * into a `.doc` without three different escapings of the same words"*. §9's letterhead is
 * three cells — the competition mark | the full competition name | the governing body's mark.
 *
 * **The two plates hold real artwork as of 2026-09-10.** They were placeholders with a caption
 * saying what would fill them, and the Technical Delegate printed a hearing letter at the venue
 * with both still empty. They now draw the `emblem` and `governing` brand assets through
 * `useBrandAsset`, and fall back to exactly the placeholder they were on an installation that
 * has uploaded neither. See {@link LetterheadPlate}.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PRINTING IS PRINTING, AND NOTHING MORE IS CLAIMED
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * §9's output chips (`✓ .doc — Record Copy`, `✓ PDF — Sent To Team Manager`) are not drawn,
 * because no `.doc` is written, no PDF is produced and nothing is sent to anybody. What
 * exists is `window.print()` over an `@media print` stylesheet that leaves the document alone
 * on the page. {@link OUTPUT_HONESTY_NOTE} says that in the place the chips would have sat.
 */

import { useEffect, useState } from "react";

import { useTournament } from "../../app/session.js";
import type { BrandAssetKindView } from "../../api/types.js";
import { useBrandAsset } from "../branding/useBrandAsset.js";
import {
  letterheadEventName,
  LETTERHEAD_CAPTION,
  OUTPUT_HONESTY_NOTE,
  REDRAFT_CONFIRM_BODY,
  REDRAFT_CONFIRM_GO,
  REDRAFT_CONFIRM_KEEP,
  REDRAFT_CONFIRM_TITLE,
  REDRAFT_LABEL,
  WORKSPACE_ISSUED_NOTE,
  WORKSPACE_PRIMARY,
  WORKSPACE_PRIMARY_CAPTION,
  aiDraftBanner,
  canRedraft,
  reopenLabel,
  workspaceChip,
  workspaceTitle,
} from "./documentText.js";
import { printHearingDocument, type HearingPrintCase } from "./hearingPrint.js";

import type { HearingBatch, HearingDocumentState } from "@hockeytd/shared";

/**
 * One cell of §9's letterhead — the competition mark on the left, the governing body's on the
 * right — drawing the stored artwork where there is some and the placeholder plate where there
 * is not.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE PLACEHOLDER TEXT IS UNCHANGED, TO THE LETTER
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * An installation that has uploaded no marks must print exactly the document it printed
 * yesterday. The empty plate keeps its dashed frame and its words — `Competition Logo`,
 * `Governing Body Logo` — because those words are the honest statement that branding is not
 * configured, and quietly emptying the plate would turn a stated absence into what reads as a
 * rendering fault.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A CONTENT `<img>`, NEVER A `background-image`
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Browsers strip background images from a print by default — that is what `print-color-adjust`
 * exists to argue with, and it is honoured unevenly. A letterhead that vanished on paper while
 * looking correct on screen is precisely the class of defect this run was opened for. So the
 * mark is an `<img>` in the document, which every printer treats as content.
 *
 * The bytes come through `useBrandAsset`, not from an `src` the browser fetches: this API is
 * credentialled and a plain `<img src="/api/brand/emblem">` arrives anonymously and renders as a
 * broken-image glyph. That module's header argues it at length. It is also a shared cache, so
 * the emblem on this letterhead is the same object URL the masthead is already drawing and the
 * document costs no second request.
 *
 * `alt` is empty and the plate stays `aria-hidden`, exactly as the placeholder was. The mark is
 * decorative here in the strict sense: the document names the event, the competition and the
 * governing body in words, in the letterhead's own centre cell and again in the body, so an alt
 * text would be a third reading of a fact the reader has already had twice.
 */
function LetterheadPlate({
  kind,
  placeholder,
  testId,
}: {
  kind: BrandAssetKindView;
  placeholder: string;
  testId: string;
}): React.JSX.Element {
  const { asset } = useBrandAsset(kind);

  // Loading draws the placeholder too, for `useBrandAsset`'s reason: there is no fourth state,
  // and a letterhead must never flash a hole in the middle of a document being read aloud in a
  // hearing room.
  if (asset === null) {
    return (
      <span className="docpaper__logo" aria-hidden="true" data-testid={testId}>
        {placeholder}
      </span>
    );
  }

  return (
    <span
      className="docpaper__logo docpaper__logo--set"
      aria-hidden="true"
      data-testid={testId}
    >
      <img className="docpaper__mark" src={asset.url} alt="" data-testid={`${testId}-image`} />
    </span>
  );
}

/**
 * §9's paper letterhead, over whatever the workspace is showing.
 *
 * The three cells are §9's own: the competition mark left, the full competition name centred,
 * the governing body's mark right. Both marks are stored brand assets as of 2026-09-10 —
 * Configuration §01's third upload row — and were placeholder plates before that.
 *
 * The caption is outside the printable body — see the `@media print` block in
 * `editorial.css`, which hides `.docpaper__caption`: it is guidance for the TD at the desk
 * and has no business on a document served on a Team Manager.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * `batch` IS PRESENTATION ONLY, AND ONLY BATCH 3 USES IT — RUN 91
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The Technical Delegate hand-polished a printed Decision Letter on 2026-09-21 and asked for
 * the application's own print of every future one to come out the same way: the letterhead
 * stacked and centred rather than laid across three columns, and the body set in a book face.
 * That is a look for the DECISION LETTER and for nothing else — the Hearing Letter's printed
 * three-column letterhead is RUN 88's fix, shipped the day before after he printed one at the
 * venue, and the minutes are a form, not a letter.
 *
 * So the paper takes the batch it is carrying and adds `docpaper--decision` on batch 3 alone.
 * The prop is optional and absent means *"not the decision letter"*: `MinutesForm` composes its
 * own paper around a form and passes nothing, which is the correct answer for it, and the two
 * places that know they are rendering a numbered document pass the number they already hold.
 * Nothing here reads or reshapes a single word of the generated body — `hearing-documents.ts`
 * remains the only author of that text.
 */
export function DocumentPaper({
  batch,
  children,
}: {
  /** The document being rendered, where the caller knows it. Batch 3 alone is styled. */
  batch?: HearingBatch | undefined;
  children: React.ReactNode;
}): React.JSX.Element {
  const event = letterheadEventName(useTournament());
  return (
    <div
      className={`docpaper printdoc${batch === 3 ? " docpaper--decision" : ""}`}
      data-testid="documentPaper"
    >
      <div className="docpaper__head">
        <LetterheadPlate
          kind="emblem"
          placeholder="Competition Logo"
          testId="letterhead-competition"
        />
        {/* The event, from the tournament record. Absent rather than defaulted when the
            installation has none — the caption below already says branding is pending. */}
        <span className="docpaper__event" data-testid="letterhead-event">
          {event}
        </span>
        <LetterheadPlate
          kind="governing"
          placeholder="Governing Body Logo"
          testId="letterhead-governing"
        />
      </div>
      <p className="docpaper__caption">{LETTERHEAD_CAPTION}</p>
      {children}
    </div>
  );
}

/**
 * The read-only document view §9 requires — real text in the DOM, never a textarea.
 *
 * `white-space: pre-wrap` keeps the generators' own line breaks, which are load-bearing: a
 * signature block, an address block and §12's `RECEIVED:` service block are all shaped by
 * them.
 */
export function DocumentView({
  body,
  testId,
}: {
  body: string;
  testId: string;
}): React.JSX.Element {
  return (
    <div className="docbody" data-testid={testId} tabIndex={0} role="article">
      {body}
    </div>
  );
}

export interface DocumentWorkspaceProps {
  batch: HearingBatch;
  state: HearingDocumentState;
  body: string;
  /** Draft mode only. */
  onBodyChange: (next: string) => void;
  /** True when the textarea holds words the server has not been told about. */
  dirty: boolean;
  /** Stores the edited body and moves nothing — §9's *"edited by the TD"*. */
  onSaveDraft: () => void;
  /** §10's `Print`, §12's `Save`. Both store the wording first, then issue it. */
  onPrimary: () => void;
  /** §10 and §12's `Reopen For Editing`, on an issued document. */
  onReopen: () => void;
  /**
   * §10d's `Re-Draft From The Case`, on a batch 1 or batch 3 DRAFT — ruled 2026-09-10.
   *
   * Optional so that a caller which has no recomposition to offer simply does not pass it;
   * where it is absent the control is not drawn, exactly as {@link canRedraft} would refuse
   * to draw it. The panel passes it always and lets `canRedraft` decide.
   */
  onRedraft?: (() => void) | undefined;
  onClose: () => void;
  busy: boolean;
  /**
   * RUN 93 — the two case fields the saved PDF's filename is built from, where the caller holds
   * the case.
   *
   * Optional, and absent means *"no case to name this after"*: the document still prints, under
   * the shorter `DC-Hearing-Letter`, because the press is the TD's only output path and must not
   * depend on a field. The panel passes the open case always; `hearingPrintFilename` documents
   * what each missing field costs the name.
   */
  printCase?: HearingPrintCase | undefined;
}

/**
 * Batch 1 and batch 3's workspace, and the read-only view batch 2 falls back to once its
 * minutes are filed.
 *
 * Draft mode is a textarea plus §9's `AI DRAFT` banner, the primary button and its caption.
 * Issued mode is {@link DocumentView} plus `Reopen For Editing`. There is no third mode: a
 * document is being written or it has gone out.
 */
export function DocumentWorkspace({
  batch,
  state,
  body,
  onBodyChange,
  dirty,
  onSaveDraft,
  onPrimary,
  onReopen,
  onRedraft,
  onClose,
  busy,
  printCase,
}: DocumentWorkspaceProps): React.JSX.Element {
  const editable = state === "draft";

  /**
   * §10d's second press, held here and nowhere else.
   *
   * The idiom is the one the sign-out control and the appointments override box already use:
   * the first press ARMS and says what will be lost, the second press does it, and the way
   * out is the box's own button. `window.confirm` appears nowhere in this application and is
   * not introduced for the one control that destroys a TD's own wording.
   */
  const [armed, setArmed] = useState(false);
  const redraftable = canRedraft(batch, state) && onRedraft !== undefined;

  // A different batch, or a document that has moved, is a different decision. Nothing about
  // the last one stays armed.
  useEffect(() => {
    setArmed(false);
  }, [batch, state]);

  return (
    <section className="docwork" aria-label={workspaceTitle(batch, state)} data-testid="workspace">
      <div className="docwork__bar">
        <b className="docwork__title" data-testid="workspaceTitle">
          {workspaceTitle(batch, state)}
        </b>
        <span className="statuspill" data-testid="workspaceChip">
          <span className="sdot" aria-hidden="true" />
          {workspaceChip(batch, state)}
        </span>
        <span className="spacer" />
        {editable ? null : (
          <button type="button" className="gbtn" disabled={busy} onClick={onReopen}>
            {reopenLabel(batch)}
          </button>
        )}
        {redraftable ? (
          <button
            type="button"
            className="gbtn"
            disabled={busy}
            data-testid="workspaceRedraft"
            onClick={() => {
              setArmed(true);
            }}
          >
            {REDRAFT_LABEL}
          </button>
        ) : null}
        <button type="button" className="gbtn" onClick={onClose}>
          Close
        </button>
      </div>

      {/*
        * §10d'S CONFIRM STRIP — the discard is deliberate or it does not happen.
        *
        * Drawn in the page flow, directly under the bar the press came from, so the sentence
        * saying what will be lost is beside the letter that will be lost.
        */}
      {redraftable && armed ? (
        <div
          className="redraftconfirm"
          role="alertdialog"
          aria-labelledby="redraftConfirmTitle"
          data-testid="workspaceRedraftConfirm"
        >
          <b id="redraftConfirmTitle">{REDRAFT_CONFIRM_TITLE}</b>
          <p>{REDRAFT_CONFIRM_BODY}</p>
          <div className="redraftconfirm__acts">
            <button
              type="button"
              className="gbtn"
              data-testid="workspaceRedraftKeep"
              onClick={() => {
                setArmed(false);
              }}
            >
              {REDRAFT_CONFIRM_KEEP}
            </button>
            <button
              type="button"
              className="gbtn gbtn--solid"
              disabled={busy}
              data-testid="workspaceRedraftGo"
              onClick={() => {
                setArmed(false);
                onRedraft?.();
              }}
            >
              {busy ? "Working…" : REDRAFT_CONFIRM_GO}
            </button>
          </div>
        </div>
      ) : null}

      {editable ? (
        <div className="warnnote warnnote--attention" role="note" data-testid="aiDraftBanner">
          <span className="warnnote__kind">AI Draft</span>
          <span className="warnnote__msg">{aiDraftBanner(batch)}</span>
        </div>
      ) : null}

      <DocumentPaper batch={batch}>
        {editable ? (
          <textarea
            className="docbody docbody--edit"
            data-testid="documentEditor"
            aria-label={`${workspaceTitle(batch, state)} — document body`}
            value={body}
            rows={24}
            onChange={(event) => {
              onBodyChange(event.target.value);
            }}
          />
        ) : (
          <DocumentView body={body} testId="documentView" />
        )}
      </DocumentPaper>

      {editable ? (
        <div className="docwork__acts">
          <button
            type="button"
            className="gbtn gbtn--solid"
            disabled={busy}
            onClick={onPrimary}
            data-testid="workspacePrimary"
          >
            {busy ? "Working…" : WORKSPACE_PRIMARY[batch]}
          </button>
          <button
            type="button"
            className="gbtn"
            disabled={busy || !dirty}
            onClick={onSaveDraft}
            data-testid="workspaceSaveDraft"
          >
            Save Draft
          </button>
          <span className="btnhint" data-testid="workspaceDirty">
            {dirty
              ? "Unsaved edits. Save Draft stores them now; the primary button stores them " +
                "before it issues the document."
              : "The stored copy matches what is on screen."}
          </span>
          <span className="btnhint" data-testid="workspaceCaption">
            {WORKSPACE_PRIMARY_CAPTION[batch]}
          </span>
        </div>
      ) : (
        <div className="docwork__acts">
          <span className="btnhint" data-testid="workspaceIssuedNote">
            {WORKSPACE_ISSUED_NOTE[batch]}
          </span>
          <button
            type="button"
            className="gbtn"
            onClick={() => {
              /* RUN 93: dressed rather than bare — the page margin goes to zero so the
                 browser has nowhere to draw its date, title, URL and page count, and the
                 tab is renamed so `Save as PDF` offers the TD's filename. */
              printHearingDocument(batch, printCase ?? null);
            }}
            data-testid="workspacePrint"
          >
            Print
          </button>
        </div>
      )}

      <p className="docwork__honesty" data-testid="outputHonesty">
        {OUTPUT_HONESTY_NOTE}
      </p>
    </section>
  );
}
